import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import {
  CreditCard,
  Image,
  Video,
  Music,
  History,
  Plus,
  TrendingUp,
  Sparkles,
  Loader2,
  RefreshCw,
  Wallet,
  LayoutGrid,
  FileText,
  ArrowRight,
  BookOpen,
  ExternalLink
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { checkTaskStatus } from "@/lib/task-checker";
import { useToast } from "@/hooks/use-toast";

interface GenerationLog {
  id: string;
  service_type: "photo" | "video" | "music";
  model: string;
  prompt: string;
  credits_used: number;
  status: string;
  result_url: string | null;
  created_at: string;
}

interface CreditOrder {
  id: string;
  credits_amount: number;
  bonus_credits: number;
  price_thb: number;
  status: string;
  created_at: string;
  completed_at: string | null;
}

interface HistoryItem {
  id: string;
  type: "generation" | "topup";
  created_at: string;
  data: GenerationLog | CreditOrder;
}

interface Stats {
  photos: number;
  videos: number;
  music: number;
  totalCreditsUsed: number;
}

interface FeaturedArticle {
  id: string;
  title: string;
  title_en: string | null;
  slug: string;
  excerpt: string | null;
  excerpt_en: string | null;
  cover_image: string | null;
  published_at: string | null;
}

const Dashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, profile, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [recentGenerations, setRecentGenerations] = useState<GenerationLog[]>([]);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [stats, setStats] = useState<Stats>({ photos: 0, videos: 0, music: 0, totalCreditsUsed: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState<string | null>(null);
  const [featuredArticles, setFeaturedArticles] = useState<FeaturedArticle[]>([]);
  const [hasPromo1290, setHasPromo1290] = useState(false);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  // Fetch generation logs, credit orders, and stats
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      setIsLoading(true);

      // Fetch recent generations
      const { data: logs, error: logsError } = await supabase
        .from("generation_logs")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);

      if (logsError) {
        console.error("Error fetching logs:", logsError);
      } else {
        setRecentGenerations(logs as GenerationLog[]);
      }

      // Fetch recent credit top-ups (completed orders only)
      const { data: orders, error: ordersError } = await (supabase as any)
        .from("credit_orders")
        .select("id, credits_amount, bonus_credits, price_thb, status, created_at, completed_at")
        .eq("user_id", user.id)
        .eq("status", "completed")
        .order("completed_at", { ascending: false })
        .limit(10);

      if (ordersError) {
        console.error("Error fetching orders:", ordersError);
      }

      // Combine generation logs and credit orders into history items
      const combined: HistoryItem[] = [];

      if (logs) {
        logs.forEach((log: GenerationLog) => {
          combined.push({
            id: log.id,
            type: "generation",
            created_at: log.created_at,
            data: log
          });
        });
      }

      if (orders) {
        orders.forEach((order: CreditOrder) => {
          combined.push({
            id: order.id,
            type: "topup",
            created_at: order.completed_at || order.created_at,
            data: order
          });
        });
      }

      // Sort by date descending and limit to 20
      combined.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setHistoryItems(combined.slice(0, 20));

      // Fetch stats - count by type (with limit for performance)
      const { data: allLogs, error: statsError } = await supabase
        .from("generation_logs")
        .select("service_type, credits_used")
        .eq("user_id", user.id)
        .limit(5000); // Limit to last 5000 for performance - covers most users

      if (statsError) {
        console.error("Error fetching stats:", statsError);
      } else if (allLogs) {
        const newStats: Stats = { photos: 0, videos: 0, music: 0, totalCreditsUsed: 0 };
        allLogs.forEach((log: { service_type: string; credits_used: number }) => {
          if (log.service_type === "photo") newStats.photos++;
          if (log.service_type === "video") newStats.videos++;
          if (log.service_type === "music") newStats.music++;
          newStats.totalCreditsUsed += log.credits_used;
        });
        setStats(newStats);
      }

      setIsLoading(false);
    };

    const fetchFeaturedArticles = async () => {
      try {
        const { data, error } = await (supabase as any)
          .from("articles")
          .select("id, title, title_en, slug, excerpt, excerpt_en, cover_image, published_at")
          .eq("is_published", true)
          .eq("is_featured", true)
          .order("published_at", { ascending: false })
          .limit(3);

        if (!error && data) {
          setFeaturedArticles(data);
        }
      } catch (err) {
        console.error("Error fetching articles:", err);
      }
    };

    const checkPromo1290Purchase = async () => {
      try {
        const { data, error } = await (supabase as any)
          .from("credit_orders")
          .select("id")
          .eq("user_id", user.id)
          .eq("status", "completed")
          .or("package_name.ilike.%promo%,package_name.ilike.%1290%")
          .limit(1);

        if (!error && data && data.length > 0) {
          setHasPromo1290(true);
        }
      } catch (err) {
        console.error("Error checking promo 1290:", err);
      }
    };

    if (user) {
      fetchData();
      fetchFeaturedArticles();
      checkPromo1290Purchase();
    }
  }, [user]);

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "photo":
        return t("รูปภาพ", "Photo");
      case "video":
        return t("วิดีโอ", "Video");
      case "music":
        return t("เพลง", "Music");
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCheckStatus = async (logId: string) => {
    setCheckingStatus(logId);
    try {
      const result = await checkTaskStatus(logId);

      if (result.success && result.status === "completed" && result.url) {
        toast({
          title: t("✅ เสร็จแล้ว!", "✅ Completed!"),
          description: t("ผลลัพธ์พร้อมแล้ว", "Result is ready")
        });
        // Reload data
        const { data: logs } = await supabase
          .from("generation_logs")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(20);
        if (logs) setRecentGenerations(logs as GenerationLog[]);
      } else if (result.status === "processing") {
        toast({
          title: t("⏳ กำลังประมวลผล", "⏳ Processing"),
          description: t("กรุณารอสักครู่", "Please wait a moment")
        });
      } else if (result.status === "failed") {
        toast({
          title: t("❌ ล้มเหลว", "❌ Failed"),
          description: result.message,
          variant: "destructive"
        });
      }
    } catch (error: any) {
      toast({
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setCheckingStatus(null);
    }
  };

  const statsDisplay = [
    { label: t("รูปภาพที่สร้าง", "Photos Generated"), value: stats.photos, icon: Image, color: "text-cyan-500" },
    { label: t("วิดีโอที่สร้าง", "Videos Created"), value: stats.videos, icon: Video, color: "text-purple-500" },
    { label: t("เพลงที่สร้าง", "Music Tracks"), value: stats.music, icon: Music, color: "text-magenta-500" },
    { label: t("เครดิตที่ใช้", "Credits Used"), value: stats.totalCreditsUsed, icon: TrendingUp, color: "text-primary" },
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-20 pb-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-heading font-bold mb-2">
                {t("สวัสดี", "Hello")}, {profile?.display_name || user.email?.split("@")[0]}!
              </h1>
              <p className="text-muted-foreground">{t("พร้อมสร้างสรรค์สิ่งมหัศจรรย์หรือยัง?", "Ready to create something amazing?")}</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Credits Display */}
              <div className="flex flex-col gap-0.5 px-4 py-2 rounded-xl bg-primary/10 border border-primary/20 justify-center">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-gradient">
                      {isLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        (profile?.credits ?? 0).toLocaleString()
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground">{t("เครดิต", "credits")}</span>
                  </div>
                </div>
                {profile?.credits_expires_at && (
                  <p className="text-[10px] text-muted-foreground text-left pl-7">
                    {t("หมดอายุ", "Expires")}: {new Date(profile.credits_expires_at).toLocaleDateString("th-TH", {
                      year: "numeric",
                      month: "short",
                      day: "numeric"
                    })}
                  </p>
                )}
              </div>
              <Link to="/pricing">
                <Button variant="outline" className="gap-2">
                  <Plus className="h-4 w-4" />
                  {t("เพิ่มเครดิต", "Add Credits")}
                </Button>
              </Link>
              <Link to="/credit-history">
                <Button variant="outline" className="gap-2">
                  <Wallet className="h-4 w-4" />
                  {t("ประวัติการเงิน", "Transaction History")}
                </Button>
              </Link>
              <Link to="/history">
                <Button variant="outline" className="gap-2">
                  <History className="h-4 w-4" />
                  {t("ประวัติการสร้าง", "Generation History")}
                </Button>
              </Link>
              <Link to="/my-programs">
                <Button variant="outline" className="gap-2">
                  <LayoutGrid className="h-4 w-4" />
                  {t("โปรแกรมของฉัน", "My Programs")}
                </Button>
              </Link>
              <Link to="/generate">
                <Button variant="glow" className="gap-2">
                  <Sparkles className="h-4 w-4" />
                  {t("สร้างผลงาน", "Generate")}
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {statsDisplay.map((stat, index) => (
              <Card key={index} variant="glass">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center">
                      <stat.icon className={`h-5 w-5 ${stat.color}`} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stat.value}
                      </p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Promo 1290 Guide Card */}
          {hasPromo1290 && (
            <Card className="mb-8 bg-gradient-to-br from-purple-600/20 to-pink-600/20 border-purple-500/30 overflow-hidden relative">
              <div className="absolute top-0 right-0 bg-gradient-to-l from-yellow-500 to-orange-500 text-white px-3 py-1 text-xs font-bold rounded-bl-lg">
                <Sparkles className="h-3 w-3 inline mr-1" />
                {t("สิทธิพิเศษ", "Exclusive")}
              </div>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/30 to-pink-500/30 shrink-0">
                    <FileText className="h-8 w-8 text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-purple-400 mb-1">
                      {t("คู่มือเสก Sora2 ไวรัล ครั้งละเยอะๆ", "Sora2 Viral Guide - Mass Generation")}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {t(
                        "สิทธิพิเศษสำหรับผู้ซื้อแพ็คเกจ Promo 1290! เรียนรู้เทคนิคการสร้างวิดีโอไวรัลแบบครั้งละหลายๆคลิป",
                        "Exclusive for Promo 1290 buyers! Learn techniques for mass viral video generation"
                      )}
                    </p>
                    <a
                      href="https://docs.google.com/document/d/1VOC3m-YVIJ4Xm1oYYhRu4YwBOAjMYFNm/edit"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="glow"
                        className="gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      >
                        <FileText className="h-4 w-4" />
                        {t("เปิดคู่มือ", "Open Guide")}
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Link to="/generate?type=photo">
              <Card variant="glass" className="group hover:border-cyan-500/30 transition-all cursor-pointer">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                    <Image className="h-6 w-6 text-cyan-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t("สร้างรูปภาพ", "Generate Photo")}</h3>
                    <p className="text-sm text-muted-foreground">{t("เริ่มต้น 3.5 เครดิต", "From 3.5 credits")}</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/generate?type=video">
              <Card variant="glass" className="group hover:border-purple-500/30 transition-all cursor-pointer">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                    <Video className="h-6 w-6 text-purple-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t("สร้างวิดีโอ", "Create Video")}</h3>
                    <p className="text-sm text-muted-foreground">{t("เริ่มต้น 30 เครดิต", "From 30 credits")}</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/generate?type=music">
              <Card variant="glass" className="group hover:border-magenta-500/30 transition-all cursor-pointer">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-magenta-500/10 flex items-center justify-center group-hover:bg-magenta-500/20 transition-colors">
                    <Music className="h-6 w-6 text-magenta-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t("สร้างเพลง", "Compose Music")}</h3>
                    <p className="text-sm text-muted-foreground">{t("12 เครดิต", "12 credits")}</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>

          {/* Featured Articles / Announcements */}
          {featuredArticles.length > 0 && (
            <Card variant="glass" className="mb-8">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="h-5 w-5 text-primary" />
                  {t("ประกาศ", "Announcements")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {featuredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group"
                    >
                      <div className="rounded-xl border border-border/50 overflow-hidden bg-muted/30 hover:bg-muted/50 hover:border-primary/30 transition-all">
                        {article.cover_image ? (
                          <div className="aspect-video overflow-hidden">
                            <img
                              src={article.cover_image}
                              alt={article.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        ) : (
                          <div className="aspect-video bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
                            <FileText className="h-10 w-10 text-primary/50" />
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="font-semibold line-clamp-2 group-hover:text-primary transition-colors">
                            {article.title}
                          </h3>
                          {article.excerpt && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {article.excerpt}
                            </p>
                          )}
                          <div className="flex items-center gap-1 mt-3 text-xs text-primary font-medium">
                            {t("อ่านเพิ่มเติม", "Read more")}
                            <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent History */}
          <Card variant="glass">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                {t("ประวัติการใช้งาน", "Usage History")}
              </CardTitle>
              <Link to="/history">
                <Button variant="ghost" size="sm">{t("ประวัติการสร้าง", "History")}</Button>
              </Link>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : historyItems.length === 0 ? (
                <div className="text-center py-8">
                  <Sparkles className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">{t("ยังไม่มีผลงาน เริ่มสร้างเลย!", "No generations yet. Start creating!")}</p>
                  <Link to="/generate">
                    <Button variant="glow" className="mt-4">
                      {t("สร้างผลงานแรก", "Create Your First")}
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {historyItems.map((item) => {
                    if (item.type === "topup") {
                      // Credit top-up item
                      const order = item.data as CreditOrder;
                      const totalCredits = order.credits_amount + (order.bonus_credits || 0);
                      return (
                        <div
                          key={`topup-${item.id}`}
                          className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 hover:bg-green-500/20 transition-colors border border-green-500/20"
                        >
                          <div className="flex items-center gap-3">
                            <Plus className="h-5 w-5 text-green-500" />
                            <div>
                              <p className="font-medium text-green-600 dark:text-green-400">{t("เติมเครดิต", "Credit Top-up")}</p>
                              <p className="text-xs text-muted-foreground">฿{order.price_thb.toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-green-600">+{totalCredits.toLocaleString()} {t("เครดิต", "credits")}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
                          </div>
                        </div>
                      );
                    } else {
                      // Generation item
                      const gen = item.data as GenerationLog;
                      const isTaskId = gen.result_url && !gen.result_url.startsWith("http");
                      const isProcessing = (gen.status === "processing" || gen.status === "pending") && isTaskId;

                      return (
                        <div
                          key={`gen-${item.id}`}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {gen.service_type === "photo" && <Image className="h-5 w-5 text-cyan-500" />}
                            {gen.service_type === "video" && <Video className="h-5 w-5 text-purple-500" />}
                            {gen.service_type === "music" && <Music className="h-5 w-5 text-magenta-500" />}
                            <div>
                              <p className="font-medium">{getTypeLabel(gen.service_type)}</p>
                              <p className="text-xs text-muted-foreground">{gen.model}</p>
                              {isProcessing && (
                                <p className="text-xs text-yellow-600 dark:text-yellow-400 flex items-center gap-1 mt-1">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  {t("กำลังประมวลผล", "Processing")}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isProcessing && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCheckStatus(gen.id)}
                                disabled={checkingStatus === gen.id}
                              >
                                {checkingStatus === gen.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                            <div className="text-right">
                              {gen.status === "failed" ? (
                                <p className="text-sm font-medium text-green-600">0 {t("เครดิต", "credits")} <span className="text-xs">({t("คืนแล้ว", "refunded")})</span></p>
                              ) : (
                                <p className="text-sm font-medium">-{gen.credits_used} {t("เครดิต", "credits")}</p>
                              )}
                              <p className="text-xs text-muted-foreground">{formatDate(gen.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ebook Recommendation */}
          <Card variant="glass" className="mt-8 overflow-hidden">
            <CardContent className="p-0">
              <a
                href="https://www.aievergreenclass.com/ebook"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="flex flex-col md:flex-row items-center gap-6 p-6">
                  <div className="shrink-0 w-full md:w-48 rounded-xl overflow-hidden shadow-lg group-hover:shadow-xl transition-shadow">
                    <img
                      src="/coverebookbundle.jpg"
                      alt="AI Ebook Bundle"
                      className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                      <BookOpen className="h-5 w-5 text-primary" />
                      <span className="text-xs font-bold text-primary uppercase tracking-wider">
                        {t("แนะนำ", "Recommended")}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                      {t("Ebook สอนใช้ AI สร้างรายได้", "AI Money-Making Ebook")}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t(
                        "เรียนรู้วิธีใช้ AI สร้างคอนเทนต์และรายได้แบบ Passive Income จากผู้เชี่ยวชาญ",
                        "Learn how to use AI for content creation and passive income from experts"
                      )}
                    </p>
                    <Button variant="outline" className="gap-2 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      {t("ดูรายละเอียด", "View Details")}
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </a>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
